import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Music4, Film, Clapperboard, Users, Sparkles, ArrowRight, Loader2 } from "lucide-react";

const HERO = "https://images.unsplash.com/flagged/photo-1579451443170-44b3963c3341?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxjaW5lbWF0aWMlMjBtb3ZpZSUyMGNvbmNlcHQlMjBwb3J0cmFpdCUyMG5lb24lMjBjeWJlcnB1bmslMjBzdHVkaW98ZW58MHx8fHwxNzkwMDI2MTg2fDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Music4, t: "Canciones con IA", d: "Escribe una historia y elige el ritmo. La IA compone y canta." },
  { icon: Film, t: "Videos con IA", d: "Describe la escena o sube tu foto y genera clips cinematográficos." },
  { icon: Clapperboard, t: "Cortometrajes", d: "Une escena por escena con tus personajes, sin editar nada." },
  { icon: Users, t: "Tu elenco", d: "Guarda personajes con su foto y úsalos en todos tus proyectos." },
];

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center grain">
        <Loader2 className="w-7 h-7 text-[#FF334B] spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen grain relative overflow-hidden">
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(255,51,75,0.28), transparent 70%)" }} />
      <div className="pointer-events-none absolute -bottom-56 -left-40 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(255,159,46,0.16), transparent 70%)" }} />

      <header className="relative z-10 flex items-center justify-between px-5 sm:px-10 py-6">
        <Logo />
        <div className="flex items-center gap-2">
          <button data-testid="login-community-link" onClick={() => navigate("/comunidad")}
            className="text-sm font-semibold px-4 py-2 rounded-full text-[#94A3B8] hover:text-[#FF9F2E] transition-colors">
            Comunidad
          </button>
          <button data-testid="login-google-button-top" onClick={login}
            className="text-sm font-semibold px-4 py-2 rounded-full border border-white/15 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors">
            Entrar
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-10 pt-8 sm:pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <div className="mono-label mb-5">Estudio de IA · video y música</div>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
            Crea canciones, videos y
            <span className="text-[#FF334B]"> cortometrajes</span> con solo describirlos.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#94A3B8] max-w-xl leading-relaxed">
            Sube tu foto y una idea. Metáfora Play compone la música en el ritmo que elijas
            y genera el video con tus personajes — sin editar nada. Como Suno, pero completo.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button data-testid="login-google-button" onClick={login}
              className="group inline-flex items-center gap-3 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold px-7 py-3.5 rounded-full transition-colors">
              <Sparkles className="w-5 h-5" />
              Empezar con Google
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <span className="text-xs text-[#64748B] font-mono">Gratis para empezar · sin tarjeta</span>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-4 max-w-lg">
            {FEATURES.map((f) => (
              <div key={f.t} className="mfp-card p-4">
                <f.icon className="w-5 h-5 text-[#FF9F2E] mb-2" />
                <div className="font-head font-semibold text-sm">{f.t}</div>
                <div className="text-xs text-[#64748B] mt-1 leading-relaxed">{f.d}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 scan-line">
            <img src={HERO} alt="Estudio cinematográfico de IA" className="w-full h-[440px] lg:h-[560px] object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(9,10,15,0.9))" }} />
            <div className="absolute bottom-5 left-5 right-5">
              <div className="mono-label mb-1">render · 4K cinema</div>
              <div className="font-display text-3xl">TU HISTORIA, EN PANTALLA</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
